import { Fragment, ReactNode, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export interface DrawerProps {
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	children: ReactNode;
	/** Pinned footer content (e.g. action buttons) — always visible without scrolling */
	footer?: ReactNode;
	width?: 'sm' | 'md' | 'lg';
}

const useIsMobile = () => {
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const mql = window.matchMedia('(max-width: 767px)');
		setIsMobile(mql.matches);
		const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
		mql.addEventListener('change', handler);
		return () => mql.removeEventListener('change', handler);
	}, []);

	return isMobile;
};

const Drawer = ({ isOpen, onClose, title, children, footer, width = 'md' }: DrawerProps) => {
	const isMobile = useIsMobile();

	const widthClasses = {
		sm: 'max-w-sm',
		md: 'max-w-md',
		lg: 'max-w-lg',
	};

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog as="div" className="relative z-50" onClose={onClose}>
				{/* Backdrop */}
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-300"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
				</Transition.Child>

				{/* Panel */}
				<div className="fixed inset-0 overflow-hidden">
					<div className="absolute inset-0 overflow-hidden">
						{isMobile ? (
							/* Bottom sheet (mobile) */
							<div className="pointer-events-none fixed inset-x-0 bottom-0">
								<Transition.Child
									as={Fragment}
									enter="transform transition ease-in-out duration-300"
									enterFrom="translate-y-full"
									enterTo="translate-y-0"
									leave="transform transition ease-in-out duration-300"
									leaveFrom="translate-y-0"
									leaveTo="translate-y-full"
								>
									<Dialog.Panel className="pointer-events-auto w-screen">
										<div className="flex flex-col bg-white dark:bg-gray-800 shadow-2xl rounded-t-2xl max-h-[70vh]">
											{/* Drag handle indicator */}
											<div className="flex justify-center pt-3 pb-1 shrink-0">
												<div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
											</div>

											{/* Header */}
											<div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
												{title && (
													<Dialog.Title
														as="h3"
														className="text-lg font-semibold text-gray-900 dark:text-gray-100"
													>
														{title}
													</Dialog.Title>
												)}
												<button
													type="button"
													className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none ml-auto transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
													onClick={onClose}
												>
													<span className="sr-only">Close</span>
													<XMarkIcon className="h-6 w-6" />
												</button>
											</div>

											{/* Body */}
											<div className="flex-1 overflow-y-auto px-6 py-5">
												{children}
											</div>

											{/* Footer */}
											{footer && (
												<div className="shrink-0 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
													{footer}
												</div>
											)}
										</div>
									</Dialog.Panel>
								</Transition.Child>
							</div>
						) : (
							/* Side drawer (desktop) */
							<div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
								<Transition.Child
									as={Fragment}
									enter="transform transition ease-in-out duration-300"
									enterFrom="translate-x-full"
									enterTo="translate-x-0"
									leave="transform transition ease-in-out duration-300"
									leaveFrom="translate-x-0"
									leaveTo="translate-x-full"
								>
									<Dialog.Panel
										className={`pointer-events-auto w-screen ${widthClasses[width]}`}
									>
										<div className="flex h-full flex-col bg-white dark:bg-gray-800 shadow-2xl">
											{/* Header */}
											<div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
												{title && (
													<Dialog.Title
														as="h3"
														className="text-lg font-semibold text-gray-900 dark:text-gray-100"
													>
														{title}
													</Dialog.Title>
												)}
												<button
													type="button"
													className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none ml-auto transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
													onClick={onClose}
												>
													<span className="sr-only">Close</span>
													<XMarkIcon className="h-6 w-6" />
												</button>
											</div>

											{/* Body */}
											<div className="flex-1 overflow-y-auto px-6 py-5">
												{children}
											</div>

											{/* Footer */}
											{footer && (
												<div className="shrink-0 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
													{footer}
												</div>
											)}
										</div>
									</Dialog.Panel>
								</Transition.Child>
							</div>
						)}
					</div>
				</div>
			</Dialog>
		</Transition>
	);
};

export default Drawer;
